import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Search, Plus, Check } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * ContentTagPicker
 * Chip multi-select con:
 * - ricerca istantanea
 * - filtro automatico per arma (universali sempre visibili)
 * - creazione inline "+ Crea nuovo contenuto"
 * - dedupe automatico lato server (409 → suggerisce esistente)
 *
 * Props:
 *   value: string[]            (slug list)
 *   onChange: (slug[]) => void
 *   weapon?: string            ("spada"|"sciabola"|"fioretto"|null)
 *   maxHeight?: string
 */
export default function ContentTagPicker({ value = [], onChange, weapon = null, maxHeight = "260px" }) {
  const [tags, setTags] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    try {
      const params = { active_only: true };
      if (weapon) params.weapon = weapon;
      const { data } = await api.get("/training/content-tags", { params });
      setTags(data);
    } catch { /* silent */ }
  }, [weapon]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return tags;
    const needle = q.trim().toLowerCase();
    return tags.filter((t) => (t.name || "").toLowerCase().includes(needle));
  }, [tags, q]);

  const toggle = (slug) => {
    if (value.includes(slug)) onChange(value.filter((s) => s !== slug));
    else onChange([...value, slug]);
  };

  const createInline = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/training/content-tags", { name: newName.trim(), weapon });
      setTags((prev) => [data, ...prev.filter((t) => t.id !== data.id)]);
      onChange([...value, data.slug]);
      setNewName(""); setCreating(false);
      toast.success(`"${data.name}" aggiunto`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.suggested) {
        toast.info(`Simile a "${detail.suggested.name}" — uso quello.`);
        onChange([...value, detail.suggested.slug]);
        setNewName(""); setCreating(false);
      } else {
        toast.error("Impossibile creare il contenuto");
      }
    }
  };

  return (
    <div className="space-y-2" data-testid="content-tag-picker">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="ctp-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca contenuto"
            className="h-10 pl-9"
          />
        </div>
        <button
          data-testid="ctp-create-toggle"
          onClick={() => setCreating((v) => !v)}
          className="h-10 px-3 rounded-md border border-dashed border-zinc-300 text-sm text-zinc-700 hover:border-zinc-400 flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />Nuovo
        </button>
      </div>

      {creating && (
        <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-md p-2">
          <Input
            data-testid="ctp-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createInline()}
            placeholder="Nome del nuovo contenuto"
            className="h-10"
            autoFocus
          />
          <button
            data-testid="ctp-new-save"
            onClick={createInline}
            className="h-10 px-3 rounded-md bg-black text-white text-sm hover:bg-zinc-800"
          ><Check className="w-4 h-4" /></button>
          <button onClick={() => { setCreating(false); setNewName(""); }} className="h-10 px-2 text-zinc-500 hover:text-black">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 overflow-y-auto" style={{ maxHeight }}>
        {filtered.map((t) => {
          const sel = value.includes(t.slug);
          return (
            <button
              key={t.id}
              data-testid={`ctp-tag-${t.slug}`}
              onClick={() => toggle(t.slug)}
              className={`h-9 px-3 rounded-full text-sm border transition ${
                sel
                  ? "bg-black text-white border-black"
                  : "bg-white text-zinc-800 border-zinc-300 hover:border-zinc-500"
              }`}
            >
              {sel && <Check className="w-3 h-3 mr-1 inline-block" />}
              {t.name}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-sm text-zinc-500 py-4">Nessun contenuto trovato.</div>
        )}
      </div>
    </div>
  );
}
